import { inject, Injectable } from '@angular/core';
import {
  DataModel,
  HttpFilter,
  RestService,
  RestSettings,
} from '@infodata-group/inf-commons';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { ToolsDtoFactory } from '../../engine/tools/tools-dto.factory';
import { BoCartographyGroupInterface } from '../../module/cartography/interface/back-office/bo-cartography-group.interface';
import { BoCartographyResourceSiteInterface } from '../../module/cartography/interface/back-office/cartography-resource/bo-cartography-resource-site.interface';
import {
  MapSitesClearAll,
  MapSitesDeleteDataModel,
  MapSitesDeleteGroups,
  MapSitesDeleteSites,
  MapSitesLoadDataModel,
  MapSitesLoadGroups,
  MapSitesLoadGroupsAndResources,
  MapSitesLoadSites,
  MapSitesRefreshSites,
} from '../actions/map-sites.actions';

const REST_SETTINGS: RestSettings = {
  showLoading: false,
};

const DEFAULT_STATE = {
  resources: [],
  resourcesDateLoad: null,
  resourcesDateRefresh: null,
  isResourcesLoading: false,
  groups: [],
  groupsDateLoad: null,
  isGroupsLoading: false,
  dataModel: new DataModel(),
  dataModelDateLoad: null,
  isDataModelLoading: false,
};

export class MapSitesStateModel {
  resources: BoCartographyResourceSiteInterface[] = [];
  resourcesDateLoad: number;
  resourcesDateRefresh: number;
  groups: BoCartographyGroupInterface[] = [];
  groupsDateLoad: number;
  isResourcesLoading: boolean;
  isGroupsLoading: boolean;
  dataModel: DataModel;
  dataModelDateLoad: number;
  isDataModelLoading: boolean;
}

@State<MapSitesStateModel>({
  name: 'mapSites',
  defaults: DEFAULT_STATE,
})
@Injectable()
export class MapSitesState {
  private restService = inject(RestService);

  @Action(MapSitesLoadGroups)
  loadGroups(state: StateContext<MapSitesStateModel>) {
    state.patchState({
      isGroupsLoading: true,
    });
    return this.restService
      .getAll(
        ToolsDtoFactory.getCartographyGroupSiteDtoFactory(),
        REST_SETTINGS,
      )
      .pipe(
        tap((groups) => {
          if (!groups.find((g) => g.id === 'XXX')) {
            groups.push({
              id: 'XXX',
              label: 'Groupe inconnu',
            });
          }
          state.patchState({
            groups: groups,
            groupsDateLoad: new Date().getTime(),
            isGroupsLoading: false,
          });
        }),
        catchError(() => {
          state.patchState({
            isGroupsLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapSitesLoadSites)
  loadResources(state: StateContext<MapSitesStateModel>) {
    state.patchState({
      isResourcesLoading: true,
    });
    return this.restService
      .getAll(ToolsDtoFactory.getCartographySiteDtoFactory(), REST_SETTINGS)
      .pipe(
        tap((sites) => {
          state.patchState({
            resources: sites,
            resourcesDateLoad: new Date().getTime(),
            resourcesDateRefresh: new Date().getTime(),
            isResourcesLoading: false,
          });
        }),
        catchError(() => {
          state.patchState({
            isResourcesLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapSitesRefreshSites)
  refreshResources(state: StateContext<MapSitesStateModel>) {
    state.patchState({
      isResourcesLoading: true,
    });
    const httpFilter = new HttpFilter();
    if (state.getState().resourcesDateRefresh) {
      httpFilter.setFilters('timestamp', state.getState().resourcesDateRefresh);
    }
    return this.restService
      .getAll(ToolsDtoFactory.getCartographySiteDtoFactory(), {
        ...REST_SETTINGS,
        httpFilter,
      })
      .pipe(
        tap((sites) => {
          let newSites = [...state.getState().resources];
          sites.forEach((site) => {
            switch (site.storeAction) {
              case 'create':
                newSites.push(site);
                break;
              case 'update':
                newSites = this.updateResources(site, newSites);
                break;
              case 'delete':
                newSites = this.deleteResource(site, newSites);
                break;
              default:
                newSites.push(site);
                break;
            }
          });
          state.patchState({
            resources: newSites,
            resourcesDateRefresh: new Date().getTime(),
            isResourcesLoading: false,
          });
        }),
        catchError(() => {
          state.patchState({
            isResourcesLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapSitesDeleteSites)
  deleteResources(state: StateContext<MapSitesStateModel>) {
    state.patchState({
      resources: [],
      resourcesDateLoad: null,
      resourcesDateRefresh: null,
    });
  }

  @Action(MapSitesDeleteGroups)
  deleteGroups(state: StateContext<MapSitesStateModel>) {
    state.patchState({
      groups: [],
      groupsDateLoad: null,
    });
  }

  @Action(MapSitesLoadGroupsAndResources)
  loadGroupsAndResources(state: StateContext<MapSitesStateModel>) {
    state.dispatch(new MapSitesLoadGroups()).subscribe({
      next: () => {
        state.dispatch(new MapSitesLoadSites());
      },
    });
  }

  @Action(MapSitesLoadDataModel)
  loadDataModelSite(state: StateContext<MapSitesStateModel>) {
    state.patchState({
      isDataModelLoading: true,
    });
    return this.restService
      .options(
        `${ToolsDtoFactory.getCartographySiteDtoFactory().getApi()}?PRF=INFOBULLE`,
        REST_SETTINGS,
      )
      .pipe(
        tap((dataModel) => {
          state.patchState({
            dataModel: dataModel,
            dataModelDateLoad: new Date().getTime(),
            isDataModelLoading: false,
          });
        }),
      );
  }

  @Action(MapSitesDeleteDataModel)
  deleteDataModelSite(state: StateContext<MapSitesStateModel>) {
    state.patchState({
      dataModel: new DataModel(),
      dataModelDateLoad: null,
    });
  }

  @Action(MapSitesClearAll)
  clearAll(state: StateContext<MapSitesStateModel>) {
    state.patchState(DEFAULT_STATE);
  }

  private updateResources(
    site: BoCartographyResourceSiteInterface,
    sites: BoCartographyResourceSiteInterface[],
  ): BoCartographyResourceSiteInterface[] {
    const index = sites.findIndex((s) => s.id === site.id);
    if (index > -1) {
      sites[index] = site;
    }
    return sites;
  }

  private deleteResource(
    site: BoCartographyResourceSiteInterface,
    sites: BoCartographyResourceSiteInterface[],
  ): BoCartographyResourceSiteInterface[] {
    const indexToDelete = sites.findIndex((s) => s.id === site.id);
    if (indexToDelete > -1) {
      sites.splice(indexToDelete, 1);
    }
    return sites;
  }
}
